// Which person (or truck) a file is for, read from its name.
//
// Fifty I-9s arrive as fifty files somebody named by hand: "Dana Reyes I-9",
// "I9_Reyes_Dana", "REYESDANA.pdf", "i9 - dana reyes (signed)". Filing them
// one by one is fifty trips through the upload dialog; this guesses the
// owner for each so the batch is checked once, as a table, and filed.
//
// It is deliberately cautious. A whole-word match on every part of the name
// is a match; first and last run together ("ReyesDana", "DanaReyes") is a
// match; the surname alone, when only one person has it, is a suggestion the
// table asks you to check. A short name is never matched inside another
// word, so "Dan" does not land on "Standard".

export interface OwnerCandidate { ownerId: string; label: string }

export interface OwnerMatch {
  owner: OwnerCandidate | null;
  /** True only for a full match on one person; anything else is checked by eye. */
  sure: boolean;
}

const fold = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

/** "I9_Reyes-Dana (signed).PDF" → ['i9', 'reyes', 'dana', 'signed'] */
export function nameWords(text: string): string[] {
  return fold(text).replace(/\.[a-z0-9]{2,5}$/, '').split(/[^a-z0-9]+/).filter(Boolean);
}

export function matchOwner(fileName: string, owners: OwnerCandidate[]): OwnerMatch {
  const words = new Set(nameWords(fileName));
  const joined = nameWords(fileName).join('');

  const full: OwnerCandidate[] = [];
  const surname: OwnerCandidate[] = [];
  for (const owner of owners) {
    const parts = nameWords(owner.label);
    if (!parts.length) continue;
    const all = parts.every((p) => words.has(p));
    const together = parts.length > 1
      && (joined.includes(parts.join('')) || joined.includes([...parts].reverse().join('')));
    if (all || together) full.push(owner);
    else if (parts.length > 1 && parts[parts.length - 1].length >= 3 && words.has(parts[parts.length - 1])) surname.push(owner);
  }

  // The most specific full match wins: "Dana Reyes Jr" over "Dana Reyes"
  // when the file says Jr; two people with the same full name stay a
  // question.
  if (full.length) {
    const longest = Math.max(...full.map((o) => nameWords(o.label).length));
    const best = full.filter((o) => nameWords(o.label).length === longest);
    return best.length === 1 ? { owner: best[0], sure: true } : { owner: null, sure: false };
  }
  if (surname.length === 1) return { owner: surname[0], sure: false };
  return { owner: null, sure: false };
}
